import React, { useMemo, useReducer } from "react";

import { FormList, Text } from "@performyard/py-components";

import { AddCompetencyPicker } from "./AddCompetencyPicker";
import AddCompetencyRow from "./AddCompetencyRow";

import { useCompetencies } from "../../../../api/hooks/defs/competencies";

import type { Competency } from "../../../../api/services/defs/competencies";

import type { RoleCompetencyEntry } from "./validationSchema";

const HELPER_TEXT =
	"Choose competencies for this role. Company-wide competencies can't be removed, but their expected level is optional.";

// Cap competency lookup at 500 active competencies. Companies that exceed this will see "Competency
// not found" placeholders for IDs missing from the lookup map. Long-term: paginate or use a
// server-side picker (mirrors the EmployeeSelection pattern).
const COMPETENCY_LOOKUP_LIMIT = 500;

const RoleCompetencyList: React.FC = () => {
	const { data, isLoading } = useCompetencies({ status: "active", limit: COMPETENCY_LOOKUP_LIMIT });
	// PY-12810: TanStack form's `mode="array"` field subscription tracks length only; in-place
	// edits via onChange/replaceValue update form state but don't re-trigger FormList's children
	// (length unchanged, meta already dirty). Bumping this counter on a level change forces this
	// component to re-render, which re-runs the FormList render-prop with the fresh field value.
	const [, bumpRender] = useReducer((c: number) => c + 1, 0);

	const competenciesById = useMemo(() => {
		const map = new Map<string, Competency>();
		(data?.competencies ?? []).forEach((c) => map.set(c._id, c));
		return map;
	}, [data]);

	return (
		<FormList<RoleCompetencyEntry>
			id="competencies"
			labelPosition="top"
			label={
				<div className="flex flex-col gap-1">
					<Text size="paragraph-1" weight="semi-bold" color="neutral-9">
						Competencies
					</Text>
					<Text size="paragraph-3" weight="regular" color="neutral-7">
						{HELPER_TEXT}
					</Text>
				</div>
			}
			input={({ value, onChange, pushValue, removeValue }) => {
				const entries = value || [];

				const addedIds = new Set(entries.map((e) => e.competency_id));
				const availableForPicker = (data?.competencies ?? []).filter(
					(c) => !c.is_company_wide && c.status === "active" && !addedIds.has(c._id)
				);

				const handleAdd = (entry: RoleCompetencyEntry) => {
					pushValue(entry);
				};

				const handleLevelChange = (index: number, level: number | null) => {
					if (!entries[index]) return;
					onChange(entries.map((e, i) => (i === index ? { ...e, expected_level: level } : e)));
					bumpRender();
				};

				return (
					<div className="flex flex-col gap-3 pt-2">
						<AddCompetencyPicker
							availableCompetencies={availableForPicker}
							onAdd={handleAdd}
							disabled={isLoading}
						/>

						{entries.length > 0 ? (
							<div className="flex flex-col gap-2">
								{entries.map((entry, index) => (
									<AddCompetencyRow
										key={entry.competency_id}
										index={index}
										competency={competenciesById.get(entry.competency_id)}
										expectedLevel={entry.expected_level}
										onChangeLevel={(lvl) => handleLevelChange(index, lvl)}
										onRemove={() => removeValue(index)}
									/>
								))}
							</div>
						) : (
							!isLoading && (
								<Text size="paragraph-3" weight="regular" color="neutral-7">
									No competencies added yet.
								</Text>
							)
						)}
					</div>
				);
			}}
		/>
	);
};

export default RoleCompetencyList;
